import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { BillingUsageService } from 'src/app/views/my-space/billing-usage/billing-usage.service';
import { ExportService } from 'src/app/shared/services/export.service';
import { AiRestrictedPopoverComponent } from './ai-restricted-popover.component';
import { CreditAlertPopoverComponent } from './credit-alert-popover.component';
import { ExportStatusPopoverComponent } from './export-status-popover.component';
import { LockedFeaturePopoverComponent } from './locked-feature-popover.component';

@Component({
  selector: 'app-mini-credit-counter',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div class="mini-credit-counter px-3 py-3">
      <div class="usage-bar-labels">
        <span class="usage-bar-title">
          {{ 'Billing.Credits' | translate }}
        </span>
        <span class="usage-bar-fraction">
          <strong>{{ formatNumber(creditUsage()) }}</strong> / {{ formatNumber(creditTotal()) }}
        </span>
      </div>
      <div class="usage-bar-track">
        <div class="usage-bar-fill" [style.width.%]="creditUsagePercent"></div>
      </div>
    </div>
  `,
  styles: [
    `
      .mini-credit-counter {
        width: 100%;
      }

      .usage-bar-labels {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        margin-bottom: 8px;
      }

      .usage-bar-title {
        font-size: 13px;
        font-weight: 600;
        color: var(--skz-text-primary, #0f172a);
        text-transform: capitalize;
      }

      .usage-bar-fraction {
        font-size: 12px;
        font-weight: 500;
        color: var(--skz-text-muted, #64748b);

        strong {
          color: var(--skz-text-primary, #0f172a);
          font-weight: 600;
        }
      }

      .usage-bar-track {
        height: 6px;
        background: rgba(15, 23, 42, 0.08);
        border-radius: 999px;
        overflow: hidden;
      }

      .usage-bar-fill {
        height: 100%;
        border-radius: 999px;
        background: linear-gradient(90deg, #5a73c5, #748de1);
        transition: width 1000ms cubic-bezier(0.16, 1, 0.3, 1);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MiniCreditCounterComponent implements OnInit {
  private billingService = inject(BillingUsageService);
  private exportService = inject(ExportService);
  private overlayService = inject(DynamicOverlayService);
  private sidenavService = inject(SidenavService);
  private elementRef = inject(ElementRef);

  creditTotal = this.billingService.creditTotal;
  creditUsage = this.billingService.creditUsage;

  private exportPopoverOpen = false;

  constructor() {
    effect(() => {
      if (this.billingService.creditAlertTriggered()) {
        this.openAlertPopover();
        this.billingService.creditAlertTriggered.set(false);
      }
    });

    effect(() => {
      if (this.billingService.lockedFeatureAlertTriggered()) {
        this.openLockedFeaturePopover();
        this.billingService.lockedFeatureAlertTriggered.set(false);
      }
    });

    effect(() => {
      const status = this.exportService.exportStatus();
      if (status && !this.exportPopoverOpen) {
        this.openExportStatusPopover();
      }
    });

    effect(() => {
      if (this.billingService.aiRestrictedAlertTriggered()) {
        this.openAiRestrictedPopover();
        this.billingService.aiRestrictedAlertTriggered.set(false);
      }
    });
  }

  ngOnInit() {
    // Perform initial fetch on load (bypassing cooldown) to populate signals
    this.billingService.refreshAvailableCredits(true);
  }

  get creditUsagePercent(): number {
    const total = this.creditTotal();
    const usage = this.creditUsage();
    return total > 0 ? Math.min(100, Math.round((usage / total) * 100)) : 0;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(value);
  }

  private showPopoverWithSidenav(openPopoverFn: () => void) {
    if (!this.sidenavService.opened()) {
      this.sidenavService.open();
      // Wait for the sidenav slide/open transition to finish so position bounds settle
      setTimeout(() => {
        openPopoverFn();
      }, 400);
    } else {
      openPopoverFn();
    }
  }

  private openAlertPopover() {
    this.showPopoverWithSidenav(() => {
      this.overlayService.open(this.elementRef, CreditAlertPopoverComponent, { hasBackdrop: false });
    });
  }

  private openLockedFeaturePopover() {
    this.showPopoverWithSidenav(() => {
      this.overlayService.open(this.elementRef, LockedFeaturePopoverComponent, { hasBackdrop: false });
    });
  }

  private openAiRestrictedPopover() {
    this.showPopoverWithSidenav(() => {
      this.overlayService.open(this.elementRef, AiRestrictedPopoverComponent, { hasBackdrop: false });
    });
  }

  private openExportStatusPopover() {
    this.exportPopoverOpen = true;
    this.showPopoverWithSidenav(() => {
      const { ref } = this.overlayService.open(this.elementRef, ExportStatusPopoverComponent, {
        hasBackdrop: false,
        disableBackdropClose: true,
      });
      ref.detachments().subscribe(() => {
        this.exportPopoverOpen = false;
      });
    });
  }
}
